// Share a ranked poster card (My Top Movies, Custom Rankings): the image
// (square for feeds, story for Stories / TikTok) and — once saved and public —
// its link. Extracted from TopMoviesSharePanel, which now wraps this with its
// own URLs, copy and analytics (unchanged markup).
//
// Analytics distinguish intent from outcome: opening the share sheet or a
// social intent link is 'share_clicked'; only a native share that resolves
// reports outcome "completed" (the platform never tells us a post went live).
import { useEffect, useMemo, useState } from 'react';
import { buildShareLinks } from '../MyLists/ShareListModal';
import styles from '../TopMovies/TopMovies.module.css';

export const IMAGE_FORMATS = [
    { key: 'square', label: 'Square', hint: 'Instagram, Threads, X' },
    { key: 'story', label: 'Story', hint: 'Stories, TikTok, Reels' },
];

async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
    } catch {
        const input = document.createElement('input');
        input.value = text;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
    }
}

/**
 * @param {(format: string) => string} imageUrlFor
 * @param {string|null} shareUrl          the public link, once there is one
 * @param {string} shareText              caption for native share / social intents
 * @param {string} shareTitle             title for native link share
 * @param {(format: string) => string} filenameFor
 * @param {(format: string) => string} imageAlt
 * @param {string} linkHint               under the link row
 * @param {string} noLinkHint             when there is no public link yet
 * @param {(kind: 'share_clicked'|'image_exported', params: object) => void} onTrack
 * Optional, for image options (Custom Rankings); Top Movies passes none:
 * @param {string}   [format]          controlled image size
 * @param {(format: string) => void} [onFormatChange]
 * @param {string[]} [formats]         sizes offered (default: all of IMAGE_FORMATS)
 * @param {React.ReactNode} [children] extra image controls, under the size picker
 * @param {React.ReactNode} [before]   image controls above the size picker (a layout that decides the sizes)
 * @param {string}   [formatNote]      why a size isn't offered, under the size picker
 * @param {(format: string) => Promise<Blob>} [loadImage]  a private image fetched with the
 *        user's session (a ranking draft's preview); `imageUrlFor` is then only its cache key
 *        and no image URL is ever put on the page
 */
export default function PosterSharePanel({
    imageUrlFor, shareUrl = null, shareText, shareTitle, filenameFor, imageAlt, linkHint, noLinkHint, onTrack = () => {},
    format: controlledFormat, onFormatChange, formats, children, before = null, formatNote = null, loadImage = null,
}) {
    const [ownFormat, setOwnFormat] = useState('square');
    const format = controlledFormat ?? ownFormat;
    const setFormat = onFormatChange ?? setOwnFormat;
    const offered = formats ? IMAGE_FORMATS.filter((f) => formats.includes(f.key)) : IMAGE_FORMATS;
    const [imageState, setImageState] = useState('loading');
    const [busy, setBusy] = useState(null); // 'share' | 'download' | null
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    const imageUrl = imageUrlFor(format);
    useEffect(() => { setImageState('loading'); setError(''); }, [imageUrl]);

    // A private image: fetched with the session and shown from a local object URL.
    const [blobUrl, setBlobUrl] = useState(null);
    useEffect(() => {
        if (!loadImage) return undefined;
        let cancelled = false;
        let made = null;
        setBlobUrl(null);
        Promise.resolve()
            .then(() => loadImage(format))
            .then((blob) => {
                if (cancelled) return;
                made = URL.createObjectURL(blob);
                setBlobUrl(made);
            })
            .catch(() => { if (!cancelled) setImageState('error'); });
        return () => {
            cancelled = true;
            if (made) URL.revokeObjectURL(made);
        };
        // imageUrl is the cache key: it changes with every option.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageUrl, Boolean(loadImage)]);
    const src = loadImage ? blobUrl : imageUrl;

    const links = useMemo(() => {
        if (!shareUrl) return null;
        const base = buildShareLinks({ url: shareUrl, text: shareText });
        return { ...base, threads: `https://www.threads.net/intent/post?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}` };
    }, [shareUrl, shareText]);

    const filename = filenameFor(format);
    const canShareFiles = typeof navigator !== 'undefined' && typeof navigator.canShare === 'function' && typeof File !== 'undefined';

    async function fetchImage() {
        if (loadImage) {
            try {
                return await loadImage(format);
            } catch (err) {
                throw new Error(err?.statusCode === 429 ? 'Too many images requested. Please wait a minute and try again.' : "Couldn't create the image. Please try again.");
            }
        }
        const res = await fetch(imageUrl);
        if (!res.ok) throw new Error(res.status === 429 ? 'Too many images requested. Please wait a minute and try again.' : "Couldn't create the image. Please try again.");
        return res.blob();
    }

    async function downloadImage() {
        setBusy('download');
        setError('');
        try {
            const blob = await fetchImage();
            const href = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = href;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(href), 2000);
            onTrack('image_exported', { format, channel: 'download' });
        } catch (err) {
            setError(err.message || "Couldn't download the image.");
        } finally {
            setBusy(null);
        }
    }

    async function shareImage() {
        setBusy('share');
        setError('');
        try {
            const blob = await fetchImage();
            const file = new File([blob], filename, { type: 'image/png' });
            if (!navigator.canShare({ files: [file] })) {
                setBusy(null);
                return downloadImage();
            }
            onTrack('share_clicked', { format, channel: 'image_native' });
            try {
                await navigator.share({ files: [file], ...(shareUrl ? { text: `${shareText} ${shareUrl}` } : { text: shareText }) });
                onTrack('image_exported', { format, channel: 'native_share', outcome: 'completed' });
            } catch (shareErr) {
                if (shareErr?.name !== 'AbortError') throw shareErr;
            }
        } catch (err) {
            setError(err.message || "Couldn't share the image.");
        } finally {
            setBusy(null);
        }
    }

    const trackLink = (channel) => () => onTrack('share_clicked', { channel });

    async function copyLink() {
        await copyText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
        trackLink('copy_link')();
    }

    async function nativeShareLink() {
        if (!navigator.share) return copyLink();
        trackLink('native')();
        try {
            await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
            onTrack('share_clicked', { channel: 'native', outcome: 'completed' });
        } catch { /* dismissed */ }
        return undefined;
    }

    return (
        <div className={styles.sharePanel}>
            <div className={`${styles.previewWrap} ${format === 'story' ? styles.previewStory : ''}`}>
                {imageState === 'loading' && <span className={styles.previewLoading} role="status">Rendering your poster…</span>}
                {imageState === 'error' && <span className={styles.previewLoading} role="alert">Preview unavailable — try again in a moment.</span>}
                {src && (
                    <img
                        key={imageUrl}
                        src={src}
                        alt={imageAlt(format)}
                        onLoad={() => setImageState('ready')}
                        onError={() => setImageState('error')}
                        style={imageState === 'ready' ? undefined : { opacity: 0 }}
                    />
                )}
            </div>

            <div className={styles.shareControls}>
                {before}
                <div>
                    <p className={styles.sectionLabel}>Image size</p>
                    <div className={styles.segmented} role="group" aria-label="Image size">
                        {offered.map((f) => (
                            <button key={f.key} type="button" aria-pressed={format === f.key} onClick={() => setFormat(f.key)} title={f.hint}>{f.label}</button>
                        ))}
                    </div>
                    <p className={styles.hint} style={{ marginTop: 6 }}>{IMAGE_FORMATS.find((f) => f.key === format)?.hint}</p>
                    {formatNote && <p className={styles.hint} style={{ marginTop: 4 }}>{formatNote}</p>}
                </div>

                {children}

                <div className={styles.buttonRow}>
                    {canShareFiles && (
                        <button type="button" className={styles.primary} onClick={shareImage} disabled={Boolean(busy)} aria-busy={busy === 'share'}>
                            {busy === 'share' ? <><span className={styles.spinner} aria-hidden="true" />Preparing image…</> : 'Share image'}
                        </button>
                    )}
                    <button type="button" className={canShareFiles ? styles.secondary : styles.primary} onClick={downloadImage} disabled={Boolean(busy)} aria-busy={busy === 'download'}>
                        {busy === 'download' ? <><span className={styles.spinner} aria-hidden="true" />Preparing image…</> : 'Download image'}
                    </button>
                </div>
                {error && <p className={styles.error} role="alert">{error}</p>}

                {links ? (
                    <div>
                        <p className={styles.sectionLabel}>Share your link</p>
                        <div className={styles.linkRow}>
                            <input value={shareUrl} readOnly aria-label="Share link" onFocus={(e) => e.target.select()} />
                            <button type="button" className={styles.secondary} onClick={copyLink}>{copied ? 'Copied!' : 'Copy'}</button>
                        </div>
                        <div className={styles.channels} style={{ marginTop: 10 }}>
                            <button type="button" onClick={nativeShareLink}>Share…</button>
                            <a href={links.threads} target="_blank" rel="noopener noreferrer" onClick={trackLink('threads')}>Threads</a>
                            <a href={links.x} target="_blank" rel="noopener noreferrer" onClick={trackLink('x')}>X</a>
                            <a href={links.facebook} target="_blank" rel="noopener noreferrer" onClick={trackLink('facebook')}>Facebook</a>
                            <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" onClick={trackLink('whatsapp')}>WhatsApp</a>
                            <a href={links.sms} onClick={trackLink('sms')}>Text</a>
                        </div>
                        <p className={styles.hint} style={{ marginTop: 8 }}>{linkHint}</p>
                    </div>
                ) : (
                    <p className={styles.hint}>{noLinkHint}</p>
                )}
            </div>
        </div>
    );
}
